import { Trash2, Package, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtKm, fmtPct } from "@/lib/wholesale-format";
import { editorItemKey, editorTotals, unitPriceOf, type EditorItem } from "@/lib/wholesale-items";
import { Stepper } from "@/components/wholesale/stepper";
import { cn } from "@/lib/utils";

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn("flex items-baseline justify-between gap-4", strong && "border-t pt-2")}>
      <span className={cn("text-[10px] uppercase tracking-[0.08em] text-muted-foreground", strong && "text-foreground")}>
        {label}
      </span>
      <span className={cn("tabular-nums", strong ? "text-lg font-semibold" : "text-xs")}>{value}</span>
    </div>
  );
}

/** Korpa prezentacije: pregled skupljenog, ispravka količina i predaja u nacrt narudžbe. */
export function ShowroomCartSheet({
  open,
  onOpenChange,
  items,
  vatRate,
  partnerName,
  saving,
  onSetQuantity,
  onRemove,
  onCreateOrder,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: EditorItem[];
  vatRate: number;
  partnerName: string;
  saving: boolean;
  onSetQuantity: (key: string, qty: number) => void;
  onRemove: (key: string) => void;
  onCreateOrder: () => void;
}) {
  const totals = editorTotals(items, vatRate);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88vh] gap-0 p-0">
        <div className="mx-auto flex max-h-[88vh] w-full max-w-3xl flex-col">
          <SheetHeader className="border-b p-4 pb-3 pr-12">
            <SheetTitle className="text-sm">Korpa</SheetTitle>
            <SheetDescription className="text-[10px] uppercase tracking-[0.08em]">
              {partnerName || "Bez partnera"}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto divide-y">
            {items.length === 0 ? (
              <p className="p-10 text-center text-xs text-muted-foreground">
                Korpa je prazna. Dodirni artikal u izlogu da ga dodaš.
              </p>
            ) : (
              items.map((it) => {
                const key = editorItemKey(it);
                const unit = unitPriceOf(it);
                return (
                  <div key={key} className="flex items-start gap-3 p-3">
                    {it.imageUrl ? (
                      <img src={it.imageUrl} alt="" className="h-16 w-16 shrink-0 object-cover ring-1 ring-foreground/10" />
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center bg-muted ring-1 ring-foreground/10">
                        <Package className="h-5 w-5 text-muted-foreground/40" />
                      </div>
                    )}

                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="line-clamp-2 text-[13px] leading-snug">{it.name || "Bez naziva"}</span>
                      <span className="text-[10px] uppercase tracking-[0.08em] tabular-nums text-muted-foreground">
                        {it.sku || "bez SKU"} · {fmtKm(unit)}
                        {it.discountPct > 0 && ` · rabat ${fmtPct(it.discountPct)}`}
                      </span>
                      <div className="mt-1 flex items-center gap-2">
                        <Stepper value={it.quantity} onChange={(n) => onSetQuantity(key, n)} />
                        <span className="ml-auto text-base font-semibold tabular-nums">
                          {fmtKm(unit * it.quantity)}
                        </span>
                        <button
                          type="button"
                          onClick={() => onRemove(key)}
                          aria-label={`Ukloni ${it.name}`}
                          className="flex h-11 w-11 items-center justify-center text-muted-foreground transition-colors active:bg-muted"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {items.length > 0 && (
            <div className="flex flex-col gap-2 border-t p-4">
              <Row label="Osnovica" value={fmtKm(totals.subtotal)} />
              <Row label="Rabat" value={`− ${fmtKm(totals.discountTotal)}`} />
              <Row label={`PDV ${fmtPct(vatRate)}`} value={fmtKm(totals.vat)} />
              <Row label="Ukupno" value={fmtKm(totals.total)} strong />
              <button
                type="button"
                onClick={onCreateOrder}
                disabled={saving}
                className="mt-1 flex h-12 items-center justify-center gap-2 bg-foreground text-sm font-medium text-background transition-opacity active:opacity-80 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Napravi predračun
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
